import { ID, Medium } from '@ianesthesie-kit/services';
import {
  AutocompleteQuestionOption,
  Choice,
  Question
} from '@/modules/questionnaire/model/question.model';
import { QuestionnaireService } from '@/modules/questionnaire';

export enum AnswerType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  CHOICE = 'CHOICE',
  UPLOAD = 'UPLOAD',
  RATING = 'RATING',
  STATEMENT = 'STATEMENT',
  _AUTOCOMPLETE = '_AUTOCOMPLETE' // only for frontend usage. CHOICE should be used for requests to backend
}

interface AnswerBase<V> {
  type: AnswerType;
  questionId: Question['id'];
  value: V;
  manuallySkipped?: boolean;
}

export interface TextAnswer extends AnswerBase<string | null> {
  type: AnswerType.TEXT;
}

export interface NumberAnswer extends AnswerBase<number | null> {
  type: AnswerType.NUMBER;
}

export interface DateAnswer extends AnswerBase<string | null> {
  type: AnswerType.DATE;
}

export interface ChoiceAnswer extends AnswerBase<Choice[] | null> {
  type: AnswerType.CHOICE;
}

/** Only for frontend usage. Should be translated to ChoiceAnswer before submitting */
export interface AutocompleteAnswer
  extends AnswerBase<AutocompleteQuestionOption[] | null> {
  type: AnswerType._AUTOCOMPLETE;
}

export interface UploadAnswer extends AnswerBase<Array<ID> | null> {
  type: AnswerType.UPLOAD;
  mediumDtos?: Medium[];
  postponed?: boolean;
  manualEntrySelected?: boolean;
  manualEntrySkipped?: boolean;
}

export interface RatingAnswer extends AnswerBase<number | null> {
  type: AnswerType.RATING;
}

export interface StatementAnswer extends AnswerBase<null> {
  type: AnswerType.STATEMENT;
}

export type Answer =
  | TextAnswer
  | NumberAnswer
  | DateAnswer
  | ChoiceAnswer
  | UploadAnswer
  | RatingAnswer
  | StatementAnswer
  | AutocompleteAnswer;

export const isTextAnswer = (ans?: Answer | null): ans is TextAnswer =>
  !!ans && ans.type === AnswerType.TEXT;

export const isNumberAnswer = (ans?: Answer | null): ans is NumberAnswer =>
  !!ans && ans.type === AnswerType.NUMBER;

export const isDateAnswer = (ans?: Answer | null): ans is DateAnswer =>
  !!ans && ans.type === AnswerType.DATE;

export const isChoiceAnswer = (ans?: Answer | null): ans is ChoiceAnswer =>
  !!ans && ans.type === AnswerType.CHOICE;

export const isUploadAnswer = (ans?: Answer | null): ans is UploadAnswer =>
  !!ans && ans.type === AnswerType.UPLOAD;

export const isRatingAnswer = (ans?: Answer | null): ans is RatingAnswer =>
  !!ans && ans.type === AnswerType.RATING;

export const isAutocompleteAnswer = (
  ans?: Answer | null
): ans is AutocompleteAnswer => !!ans && ans.type === AnswerType._AUTOCOMPLETE;

export const isStatementAnswer = (
  ans?: Answer | null
): ans is StatementAnswer => !!ans && ans.type === AnswerType.STATEMENT;

export const toChoice = (ch: Choice | string): Choice => {
  if (typeof ch !== 'string') {
    return ch;
  }

  const trimmed = ch.trim();

  return {
    id: trimmed,
    code: QuestionnaireService.USER_DEFINED_CHOICE_CODE,
    textId: trimmed
  };
};

export const toAutocompleteOption = (
  o: AutocompleteQuestionOption | string
): AutocompleteQuestionOption => {
  if (typeof o !== 'string') {
    return o;
  }

  const trimmed = o.trim();

  return {
    id: trimmed,
    code: QuestionnaireService.AUTOCOMPLETE_USER_INPUT_CODE,
    name: trimmed
  };
};
